/**
 * Zod schemas for the client forms.
 *
 * One expression of every rule the CLIENT can check, mirroring the server's
 * Pydantic models — `CreateClientBody` in
 * `admin/clients/dto.py`. Before this, the same rules lived as bespoke
 * predicates in three shapes across the client and cohort forms, and each
 * form re-derived its own touched-state booleans to decide when to show them.
 * Two defects came out of that in a single review pass: a pristine form showing
 * an error, then the fix complaining the moment a radio was clicked.
 *
 * WHAT THESE SCHEMAS DO NOT OWN, deliberately: anything needing server state.
 * Slug uniqueness is global and cross-tenant; whether a client is internal
 * decides what its cohorts may be. A schema that pretended to know those would
 * be confidently wrong in a way the operator cannot see, so they stay on the
 * server and arrive as `AdminApiError.detail` codes, rendered at the field by
 * the existing map.
 */
import { z } from 'zod'
import { tooLong } from '../common/length-message'

/** Mirrors `SlugStr` on the server: lowercase, digits, single hyphens. */
export const slugSchema = z
  .string()
  .min(1, 'A slug is required.')
  .min(2, 'Slugs are at least 2 characters.')
  .max(64, 'Slugs are at most 64 characters.')
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    'Lowercase letters, numbers and single hyphens only.',
  )

export const createClientSchema = z.object({
  name: z.string().trim().min(1, 'A client name is required.').max(200, tooLong(200)),
  slug: slugSchema,
  // Bounded rather than enumerated, like the server: 150/200/270 is a sales
  // convention, and a bespoke number should not need a deploy.
  contractTrophyTarget: z
    .number()
    // zod 3's default wording; zod 4 rewords it and a decimal can be typed.
    .int('Expected integer, received float')
    .min(1, 'Trophy target must be at least 1.')
    .max(10_000, 'Trophy target must be 10,000 or less.'),
  isInternal: z.boolean(),
  clientEmployeeIdEnabled: z.boolean().optional(),
  clientDepartmentEnabled: z.boolean().optional(),
})

export type CreateClientForm = z.infer<typeof createClientSchema>

/**
 * The detail page's edit form: the same rules. A separate name on purpose —
 * the two forms have diverged before (a removed capacity field was create-only)
 * and the call sites should not need renaming when they do again.
 */
export const updateClientSchema = createClientSchema
export type UpdateClientForm = z.infer<typeof updateClientSchema>
